/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect } from 'react'
import {
  styled,
  Drawer as MuiDrawer,
  ListItemIcon as MuiListItemIcon,
  ListItemText,
  MenuList,
  MenuItem as MuiMenuItem,
  useMediaQuery,
  useTheme,
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction as MuiBottomNavigationAction,
  Container,
  Divider,
} from '@mui/material'
import logo from '../../assets/images/FarmlandsProLogoWhite.svg'
import navBackground from '../../assets/images/nav-bg.png'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  BOTTOM_NAVIGATION_HEIGHT,
  DRAWER_DIMENSIONS,
  PAGE_PADDING_TOP,
  ScrollYPositionKeys,
  URLS,
} from '../../util/consts'
import { TestIds } from '../../testIds'
import AccountSelector from '../../features/azure-b2c/components/AccountSelector'
import { useSelector } from 'react-redux'
import MaterialSymbol from '../MaterialSymbol'
import { saveScrollYPosition, shouldForwardProp } from '../../util/helpers'
import { Declarations } from '../../features/declarations/components/DeclarationsButton'
import { Capacitor } from '@capacitor/core'
import { toast } from 'react-toastify'
import useAppInfo from '../../native/utils/AppInfo'
import onClickDeveloper from '../../util/exportAuth'
import { TermsAndConditionsText } from './TermsAndConditionsText'
import { selectRemoteConfig } from '../../services/firebase/remote-config/remoteConfig.slice'
import useMsalFunctions from '../../util/useMsalAuthFunctions'

const DrawerContainer = styled('div')(
  ({ theme }) => `
  margin: 24px 8px;
  height: 100%;
  display: flex;
  flex-direction: column;
  width: auto;

  @media (max-width: ${theme.breakpoints.values.lg}px) {
    width: auto;
    margin: 24px 8px;
  }
`,
)

const StyledAccountDetailsContainer = styled('button')(({ theme }) => ({
  fontSize: '1rem',
  color: theme.colors.primary500,
  margin: '16px 0',
  background: 'transparent',
  border: 'none',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '0.25rem',
  '& .label': {
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  '& .material-symbols-outlined': {
    fontSize: '1rem',
  },
}))

const BottomNavigation = styled(MuiBottomNavigation, {
  shouldForwardProp: (prop) => shouldForwardProp<{ appInstalled: boolean }>(['appInstalled'], prop),
})<{ appInstalled: boolean }>(({ theme, appInstalled }) => ({
  minHeight: `${BOTTOM_NAVIGATION_HEIGHT}px`,
  height: 'auto',
  paddingTop: '16px',
  paddingBottom: '16px',
  justifyContent: 'space-evenly',
  [theme.breakpoints.down('md')]: {
    paddingBottom: appInstalled ? '24px' : '16px',
  },
}))

const BottomNavigationAction = styled(MuiBottomNavigationAction)<{ href?: string }>(() => ({
  minWidth: '0',
  padding: '0',
}))

const BottomContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  padding: '8px',
  width: 'auto',
  marginTop: 'auto',
  backgroundImage: `url(${navBackground})`,
}))

const Logo = styled('img')(({ theme }) => ({
  margin: '40px auto',
  marginTop: '16px',
  [theme.breakpoints.between('md', 'lg')]: {
    margin: '31px auto',
    marginTop: '0px',
    maxWidth: '168px',
    width: '80%',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    margin: '24px auto',
    marginTop: '0px',
    padding: '0px 8px',
    width: 'auto',
    maxWidth: DRAWER_DIMENSIONS.width.sm,
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}))

const LogoWrapper = styled('div')`
  display: flex;
  justify-content: center;
`

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  transition: 'padding 0.5s ease',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}))

const ListItemIcon = styled(MuiListItemIcon)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    marginRight: 8,
  },
  '& .material-symbols-outlined': {
    fontWeight: 200,
  },
}))

const StyledMenuItem = styled(MenuItem)({
  textDecoration: 'none',
  '&.active': {
    '& .MuiMenuItem-root': {
      '& .MuiTypography-root': {
        fontWeight: 700,
      },
      '& .material-symbols-outlined': {
        fontVariationSettings: '"FILL" 1, "wght" 300, "GRAD" 0, "opsz" 48',
      },
    },
  },
})

const SmallerMuiMenuItem = styled(MuiMenuItem)(({ theme }) => ({
  '& .MuiListItemText-root': {
    '& .MuiTypography-root': {
      [theme.breakpoints.down('md')]: {
        ...theme.typography.caption,
        color: theme.colors.primary500,
      },
    },
  },
}))

const VersionNumberItem = styled(MuiMenuItem)(({ theme }) => ({
  '& .MuiListItemText-root': {
    '& .MuiTypography-root': {
      [theme.breakpoints.down('md')]: {
        ...theme.typography.caption,
        color: theme.colors.primary300,
      },
    },
  },
}))

type NavItemProps = {
  testId?: string
  to: string
  onClick?: () => void
  itemText: string
  icon: React.ReactNode
  children?: React.ReactNode | React.ReactNode[]
}

const NavItem = ({ to, onClick, itemText, icon, testId, children }: NavItemProps) => {
  const navigate = useNavigate()
  return (
    <StyledMenuItem
      farmlands-variant='dark'
      data-testid={testId}
      role='menuitem'
      onClick={() => {
        if (onClick) {
          onClick()
        } else {
          if (to === '/products')
            saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Products, position: 0 })
          else if (to === '/orders')
            saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Orders, position: 0 })
          else if (to === '/agreements')
            saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Agreements, position: 0 })
          navigate(to)
        }
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText>{itemText}</ListItemText>
      {children}
    </StyledMenuItem>
  )
}

const Nav = () => {
  const theme = useTheme()
  const isMedium = useMediaQuery(theme.breakpoints.down('md'))
  const route = useLocation()
  const navigate = useNavigate()

  const { isShareholder } = useMsalFunctions()
  const appInstalled =
    'standalone' in window.navigator && window.matchMedia('(display-mode: standalone)').matches

  const [value, setValue] = React.useState(route.pathname)

  const remoteConfig = useSelector(selectRemoteConfig)

  const sideNavItems = React.useMemo(() => {
    if (isShareholder) {
      return (
        <MenuList tabIndex={0} role='menu'>
          <NavItem
            key='/side-bar-home'
            to='/home'
            itemText='Home'
            icon={
              <MaterialSymbol
                variant={window.location.pathname.includes('/home') ? 'fill' : 'outlined'}
              >
                Home
              </MaterialSymbol>
            }
            testId={TestIds.components.sideDrawer.home}
          />
          <NavItem
            key='/side-bar-agreements'
            to='/agreements'
            itemText='Agreements'
            icon={
              <MaterialSymbol
                variant={window.location.pathname.includes('/agreement') ? 'fill' : 'outlined'}
              >
                Note
              </MaterialSymbol>
            }
            testId={TestIds.components.sideDrawer.agreements}
          />
          <NavItem
            key='/side-bar-products'
            to='/products'
            itemText={'Shop'}
            icon={
              <MaterialSymbol
                variant={window.location.pathname.includes('/product') ? 'fill' : 'outlined'}
              >
                shopping_cart
              </MaterialSymbol>
            }
            testId={TestIds.components.sideDrawer.pastProducts}
          />
          {remoteConfig.seedEnabled && (
            <NavItem
              key='/side-bar-seed'
              to='/seed'
              itemText={'Seed'}
              icon={
                <MaterialSymbol
                  variant={window.location.pathname.includes('/seed') ? 'fill' : 'outlined'}
                >
                  Psychiatry
                </MaterialSymbol>
              }
              testId={TestIds.components.sideDrawer.seed}
            />
          )}

          {remoteConfig.quoteEnabled && (
            <NavItem
              key='/side-bar-quotes'
              to='/quotes'
              itemText='Quotes'
              icon={
                <MaterialSymbol
                  variant={window.location.pathname.includes('/quotes') ? 'fill' : 'outlined'}
                >
                  request_quote
                </MaterialSymbol>
              }
              testId={TestIds.components.sideDrawer.quotes}
            />
          )}
          <NavItem
            key='/side-bar-orders'
            to='/orders'
            itemText='Orders'
            icon={
              <MaterialSymbol
                variant={window.location.pathname.includes('/order') ? 'fill' : 'outlined'}
              >
                contract
              </MaterialSymbol>
            }
            testId={TestIds.components.sideDrawer.orders}
          />
        </MenuList>
      )
    } else {
      return (
        <MenuList tabIndex={0} role='menu'>
          <NavItem
            key='/side-bar-home'
            to='/home'
            itemText='Home'
            icon={<MaterialSymbol variant='fill'>Home</MaterialSymbol>}
            testId={TestIds.components.sideDrawer.home}
          />
        </MenuList>
      )
    }
  }, [isShareholder, window.location.pathname, window.location.href, remoteConfig])
  const bottomNavItems = React.useMemo(() => {
    if (isShareholder) {
      return [
        <BottomNavigationAction
          label='Home'
          key='home'
          value='/home'
          onClick={() => navigate('/home')}
          icon={
            <MaterialSymbol
              variant={window.location.pathname.includes('/home') ? 'fill' : 'outlined'}
            >
              Home
            </MaterialSymbol>
          }
          data-testid={TestIds.components.bottomDrawer.home}
        />,
        <BottomNavigationAction
          label='Shop'
          key='products'
          value='/products'
          onClick={() => {
            saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Products, position: 0 })
            navigate('/products')
          }}
          icon={
            <MaterialSymbol
              variant={window.location.pathname.includes('/product') ? 'fill' : 'outlined'}
            >
              shopping_cart
            </MaterialSymbol>
          }
          data-testid={TestIds.components.bottomDrawer.pastProducts}
        />,
        remoteConfig.seedEnabled ? (
          <BottomNavigationAction
            label='Seed'
            key='seed'
            value='/seed'
            onClick={() => {
              saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Seeds, position: 0 })
              navigate('/seed')
            }}
            icon={
              <MaterialSymbol
                variant={window.location.pathname.includes('/seed') ? 'fill' : 'outlined'}
              >
                Psychiatry
              </MaterialSymbol>
            }
            data-testid={TestIds.components.bottomDrawer.seed}
          />
        ) : null,
        <BottomNavigationAction
          label='Orders'
          key='orders'
          value='/orders'
          onClick={() => {
            saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Orders, position: 0 })
            navigate('/orders')
          }}
          icon={
            <MaterialSymbol
              variant={window.location.pathname.includes('/orders') ? 'fill' : 'outlined'}
            >
              contract
            </MaterialSymbol>
          }
          data-testid={TestIds.components.bottomDrawer.orders}
        />,

        <BottomNavigationAction
          label='More'
          key='more'
          value='#more'
          href={window.location.href.includes('more') ? '#' : '#more'}
          icon={
            <MaterialSymbol variant={window.location.href.includes('more') ? 'fill' : 'outlined'}>
              Menu
            </MaterialSymbol>
          }
          data-testid={TestIds.components.bottomDrawer.more}
        />,
      ]
    } else
      return [
        <BottomNavigationAction
          label='Home'
          key='home'
          value='/home'
          onClick={() => navigate('/')}
          icon={<MaterialSymbol variant='fill'>Home</MaterialSymbol>}
          data-testid={TestIds.components.bottomDrawer.home}
        />,

        <BottomNavigationAction
          label='More'
          key='more'
          value='#more'
          href='#more'
          icon={<MaterialSymbol>Menu</MaterialSymbol>}
          data-testid={TestIds.components.bottomDrawer.more}
        />,
      ]
  }, [isShareholder, window.location.pathname, window.location.href])

  const { appInfoString } = useAppInfo()

  useEffect(() => {
    if (route.hash === '#more') {
      setValue(route.hash)
    } else {
      setValue(route.pathname)
    }
  }, [route])

  return (
    <>
      <MuiDrawer
        role='navigation'
        farmlands-variant='desktop'
        variant='persistent'
        open={!isMedium}
      >
        <DrawerContainer>
          <LogoWrapper onClick={Capacitor.DEBUG ? () => onClickDeveloper() : undefined}>
            <Logo src={logo} alt='Farmlands logo' />
          </LogoWrapper>
          {sideNavItems}
        </DrawerContainer>

        <BottomContainer>
          <MenuList tabIndex={0} role='menu' aria-label='Navigation Menu'>
            <NavItem
              to='/account'
              itemText={'My Account'}
              icon={<MaterialSymbol>Person</MaterialSymbol>}
              testId={TestIds.components.sideDrawer.account}
            />

            <a href={URLS.termsAndConditions} target='_blank' rel='noreferrer'>
              <StyledMenuItem
                farmlands-variant='dark'
                data-testid={TestIds.components.sideDrawer.termsAndConditions}
                role='menuitem'
              >
                <ListItemIcon>
                  <MaterialSymbol>Description</MaterialSymbol>
                </ListItemIcon>
                <TermsAndConditionsText />
              </StyledMenuItem>
            </a>
            <NavItem
              to='/logout'
              itemText={'Logout'}
              icon={<MaterialSymbol>Logout</MaterialSymbol>}
              testId={TestIds.components.sideDrawer.logout}
            />

            <NavItem
              itemText={appInfoString}
              icon={<MaterialSymbol>info</MaterialSymbol>}
              testId={TestIds.components.sideDrawer.version}
              to=''
              onClick={() => {
                navigator.clipboard
                  .writeText(appInfoString)
                  .then(() => {
                    toast('Details copied to clipboard', {
                      type: 'success',
                      icon: <MaterialSymbol>inventory</MaterialSymbol>,
                    })
                  })
                  .catch(() => {
                    toast('Copy to clipboard unsuccesful', {
                      type: 'error',
                    })
                  })
              }}
            />
          </MenuList>
        </BottomContainer>
      </MuiDrawer>

      <MuiDrawer
        farmlands-variant='mobile'
        anchor='left'
        elevation={0}
        hideBackdrop={true}
        sx={{
          height: 'calc(100%-80px)',
        }}
        open={value === '#more' && isMedium}
      >
        <Container
          sx={{
            marginTop: `calc(${PAGE_PADDING_TOP.xs}px + var(--safe-area-statusbarHeight))`,
            padding: '0px 16px',
            height: 'unset',
            overflow: 'unset',
          }}
        >
          <AccountSelector
            color={theme.colors.primary500}
            alignment='left'
            labelStyle={{ ...theme.typography.h4, fontWeight: 600, margin: 0 }}
          />
          <StyledAccountDetailsContainer onClick={() => navigate('/account')}>
            <span className='label'>View Account Details</span>
            <MaterialSymbol>east</MaterialSymbol>
          </StyledAccountDetailsContainer>
        </Container>
        <Divider sx={{ borderColor: theme.colors.primary200 }} />
        <MenuList
          sx={{ gap: 0, height: '100%', justifyContent: 'space-between' }}
          tabIndex={0}
          role='menu'
        >
          <div>
            {remoteConfig.quoteEnabled && (
              <MuiMenuItem
                farmlands-variant='light'
                onClick={() => {
                  saveScrollYPosition({
                    sessionStorageKey: ScrollYPositionKeys.Quotes,
                    position: 0,
                  })
                  navigate('/quotes')
                }}
                data-testid={TestIds.components.bottomDrawer.quotes}
              >
                <MuiListItemIcon>
                  <MaterialSymbol>request_quote</MaterialSymbol>
                </MuiListItemIcon>
                <ListItemText>Quotes</ListItemText>
              </MuiMenuItem>
            )}

            <MuiMenuItem
              farmlands-variant='light'
              onClick={() => {
                saveScrollYPosition({
                  sessionStorageKey: ScrollYPositionKeys.Agreements,
                  position: 0,
                })
                navigate('/agreements')
              }}
              data-testid={TestIds.components.bottomDrawer.agreements}
            >
              <MuiListItemIcon>
                <MaterialSymbol>Note</MaterialSymbol>
              </MuiListItemIcon>
              <ListItemText>Agreements</ListItemText>
            </MuiMenuItem>
            <MuiMenuItem
              farmlands-variant='light'
              data-testid={TestIds.components.bottomDrawer.declarations}
            >
              <Declarations>
                <MuiListItemIcon>
                  <MaterialSymbol>folder</MaterialSymbol>
                </MuiListItemIcon>
                <ListItemText>Declarations</ListItemText>
              </Declarations>
            </MuiMenuItem>
          </div>

          <div>
            <a href={URLS.termsAndConditions} target='_blank' rel='noreferrer'>
              <SmallerMuiMenuItem farmlands-variant='light'>
                <TermsAndConditionsText underline={false} />
              </SmallerMuiMenuItem>
            </a>

            <SmallerMuiMenuItem
              farmlands-variant='light'
              onClick={() => navigate('/logout')}
              data-testid={TestIds.components.bottomDrawer.logout}
            >
              <ListItemText>Log out</ListItemText>
            </SmallerMuiMenuItem>

            <VersionNumberItem
              farmlands-variant='light'
              onClick={() => {
                navigator.clipboard
                  .writeText(appInfoString)
                  .then(() => {
                    toast('Details copied to clipboard', {
                      type: 'success',
                      icon: <MaterialSymbol>inventory</MaterialSymbol>,
                    })
                  })
                  .catch(() => {
                    toast('Copy to clipboard unsuccesful', {
                      type: 'error',
                    })
                  })
              }}
            >
              <ListItemText>{appInfoString}</ListItemText>
            </VersionNumberItem>
          </div>
        </MenuList>
      </MuiDrawer>

      <BottomNavigation showLabels value={value} appInstalled={appInstalled}>
        {[...bottomNavItems]}
      </BottomNavigation>
    </>
  )
}

export default Nav
